// Renderování banneru, modálu a plovoucí ikonky do izolovaného Shadow DOM.
import { buildStyles } from './styles';
import { I18n } from './i18n';
import type {
  CategoryDef,
  CategoryId,
  CookieDef,
  ConsentState,
  CookieGobblerConfig,
} from './types';

export interface UICallbacks {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: (state: ConsentState) => void;
  /** Aktuální uložený stav souhlasu — modál jím předvyplní přepínače. */
  getCurrent?: () => ConsentState;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

export class UI {
  private host: HTMLElement;
  private root: ShadowRoot;
  private container: HTMLElement;
  private i18n: I18n;

  constructor(
    private config: CookieGobblerConfig,
    i18n: I18n,
    private cb: UICallbacks,
  ) {
    this.i18n = i18n;
    this.host = el('div');
    this.host.id = 'cookiegobbler';
    this.root = this.host.attachShadow({ mode: 'open' });

    const style = el('style');
    style.textContent = buildStyles(config.theme ?? {});
    this.root.appendChild(style);

    this.container = el('div', 'cg-root');
    this.root.appendChild(this.container);
    document.body.appendChild(this.host);
  }

  /** Aktuální stav přepínačů v modálu (pouze když je otevřený). */
  private draft: ConsentState | null = null;

  showBanner(): void {
    this.clear();
    const pos = this.config.theme?.position ?? 'bottom';

    if (this.config.theme?.overlay) {
      this.container.appendChild(el('div', 'cg-overlay'));
    }

    const banner = el('div', `cg-banner cg-pos-${pos}`);
    banner.setAttribute('role', 'dialog');
    banner.setAttribute('aria-label', this.i18n.t('banner.title'));

    const theme = this.config.theme;
    if (theme?.showLogo && theme.logo) {
      const logo = el('img', 'cg-logo');
      logo.src = theme.logo;
      logo.alt = '';
      banner.appendChild(logo);
    }

    banner.appendChild(el('h2', 'cg-title', this.i18n.t('banner.title')));
    banner.appendChild(el('p', 'cg-body', this.i18n.t('banner.body')));

    const actions = el('div', 'cg-actions');
    const settings = el('button', 'cg-btn', this.i18n.t('banner.settings'));
    settings.addEventListener('click', () => this.showModal());
    const reject = el('button', 'cg-btn', this.i18n.t('banner.rejectAll'));
    reject.addEventListener('click', () => this.cb.onRejectAll());
    const accept = el(
      'button',
      'cg-btn cg-btn-primary',
      this.i18n.t('banner.acceptAll'),
    );
    accept.addEventListener('click', () => this.cb.onAcceptAll());

    actions.append(settings, reject, accept);
    banner.appendChild(actions);
    this.container.appendChild(banner);
  }

  showModal(current?: ConsentState): void {
    this.clear();
    // Předvyplň přepínače: předaný stav → aktuální uložený souhlas → default.
    // Bez toho by reopen/„Nastavení" ukázaly vše vypnuté i po udělení souhlasu.
    this.draft = { ...(current ?? this.cb.getCurrent?.() ?? this.defaultDraft()) };

    this.container.appendChild(el('div', 'cg-overlay'));

    const modal = el('div', 'cg-modal');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    const head = el('div', 'cg-modal-head');
    head.appendChild(el('h2', undefined, this.i18n.t('modal.title')));
    head.appendChild(el('p', 'cg-modal-intro', this.i18n.t('modal.intro')));
    modal.appendChild(head);

    const body = el('div', 'cg-modal-body');
    for (const cat of this.config.categories) {
      body.appendChild(this.renderCategory(cat));
    }
    // Neklasifikované cookies — read-only skupina (bez souhlasové volby).
    const unclassified = (this.config.cookies ?? []).filter((c) => c.category === 'unclassified');
    if (unclassified.length) body.appendChild(this.renderUnclassified(unclassified));
    modal.appendChild(body);

    const foot = el('div', 'cg-modal-foot');
    const reject = el('button', 'cg-btn', this.i18n.t('modal.rejectAll'));
    reject.addEventListener('click', () => this.cb.onRejectAll());
    const acceptAll = el('button', 'cg-btn', this.i18n.t('modal.acceptAll'));
    acceptAll.addEventListener('click', () => this.cb.onAcceptAll());
    const save = el(
      'button',
      'cg-btn cg-btn-primary',
      this.i18n.t('modal.save'),
    );
    save.addEventListener('click', () => this.cb.onSave(this.draft!));

    foot.append(reject, acceptAll, el('span', 'cg-spacer'), save);
    modal.appendChild(foot);
    this.container.appendChild(modal);
  }

  private renderCategory(cat: CategoryDef): HTMLElement {
    const wrap = el('div', 'cg-cat');
    const head = el('div', 'cg-cat-head');
    head.appendChild(el('span', 'cg-cat-name', this.i18n.catName(cat.id)));

    if (cat.required) {
      head.appendChild(
        el('span', 'cg-cat-always', this.i18n.t('modal.always')),
      );
    } else {
      head.appendChild(this.renderSwitch(cat.id));
    }
    wrap.appendChild(head);
    wrap.appendChild(el('p', 'cg-cat-desc', this.i18n.catDesc(cat.id)));

    const cookies = (this.config.cookies ?? []).filter(
      (c) => c.category === cat.id,
    );
    if (cookies.length) wrap.appendChild(this.renderCookieTable(cookies));
    return wrap;
  }

  /** Read-only skupina neklasifikovaných cookies — jen výpis, bez přepínače. */
  private renderUnclassified(cookies: CookieDef[]): HTMLElement {
    const wrap = el('div', 'cg-cat');
    const head = el('div', 'cg-cat-head');
    head.appendChild(el('span', 'cg-cat-name', this.i18n.t('cat.unclassified.name')));
    wrap.appendChild(head);
    wrap.appendChild(el('p', 'cg-cat-desc', this.i18n.t('cat.unclassified.desc')));
    wrap.appendChild(this.renderCookieTable(cookies));
    return wrap;
  }

  private renderSwitch(id: CategoryId): HTMLElement {
    const label = el('label', 'cg-switch');
    const input = el('input');
    input.type = 'checkbox';
    input.checked = this.draft![id];
    input.addEventListener('change', () => {
      this.draft![id] = input.checked;
    });
    const track = el('span', 'cg-track');
    track.appendChild(el('span', 'cg-thumb'));
    label.append(input, track);
    return label;
  }

  private renderCookieTable(cookies: CookieDef[]): HTMLElement {
    const details = el('details', 'cg-cookies');
    details.appendChild(
      el('summary', undefined, this.i18n.t('modal.cookiesShown')),
    );
    const table = el('table', 'cg-cookie-table');
    const thead = el('thead');
    const trh = el('tr');
    trh.append(
      el('th', undefined, 'Cookie'),
      el('th', undefined, this.i18n.t('modal.provider')),
      el('th', undefined, this.i18n.t('modal.expiry')),
    );
    thead.appendChild(trh);
    table.appendChild(thead);

    const tbody = el('tbody');
    for (const c of cookies) {
      const tr = el('tr');
      tr.append(
        el('td', undefined, c.name),
        el('td', undefined, c.provider ?? '—'),
        el('td', undefined, c.expiry ?? '—'),
      );
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    details.appendChild(table);
    return details;
  }

  showReopen(): void {
    this.clear();
    // Ikonka vypnutá v configu → po rozhodnutí jen zavřít banner/modál.
    if (this.config.theme?.showReopen === false) return;
    const btn = el('button', 'cg-reopen');
    btn.setAttribute('aria-label', this.i18n.t('reopen.label'));
    btn.title = this.i18n.t('reopen.label');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
      '<circle cx="12" cy="12" r="9"/><circle cx="9" cy="9" r="1" fill="currentColor" stroke="none"/>' +
      '<circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/>' +
      '<circle cx="10" cy="15" r="1" fill="currentColor" stroke="none"/>' +
      '<circle cx="15" cy="15" r="1" fill="currentColor" stroke="none"/></svg>';
    btn.addEventListener('click', () => this.showModal());
    this.container.appendChild(btn);
  }

  private defaultDraft(): ConsentState {
    const s = {} as ConsentState;
    for (const cat of this.config.categories) s[cat.id] = !!cat.required;
    return s;
  }

  clear(): void {
    this.container.replaceChildren();
    this.draft = null;
  }
}
